import React from 'react'
import {
  faArrowTurnDownLeft,
  faCamera,
  faCircleXmark,
  faTag,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { AvailableMobileLoyaltyRewardResponse } from '@/lib/apis/types'
import { RATINGS } from '@/lib/social-proof'

// The checkout only charges the one-off initial assessment. The $49.90 monthly
// plan fee is charged when the patient accepts the plan, so it is not part of
// this screen; the consistency ledger step before it covers the ongoing price.

const BENEFITS = [
  'Reviewed by a doctor within 30 minutes',
  'Fill your script yourself or have it delivered to your door',
  'Unlimited check-ins with your doctor for dose adjustments',
  'No price changes on the self-dispense plan',
]

function formatCents(cents: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100)
}

function compactCents(cents: number) {
  return formatCents(cents).replace(/\.00$/, '')
}

export function WlPricingDetails({
  baseAmountCents,
  rewardResponse,
}: {
  baseAmountCents: number
  rewardResponse?: AvailableMobileLoyaltyRewardResponse
}) {
  const quote = rewardResponse?.estimatedQuote
  const appliedQuote = quote && quote.rewardAmountCents > 0 ? quote : null
  // The backend quote is authoritative when a reward applies; the CMS price can
  // lag behind Stripe for a few minutes after a price change.
  const assessmentCents = appliedQuote?.originalAmountCents ?? baseAmountCents
  const dueTodayCents = appliedQuote?.payableAmountCents ?? assessmentCents
  const reward = rewardResponse?.reward
  const offerValue =
    reward?.discountType === 'PERCENTAGE_BPS'
      ? `${reward.discountValue / 100}%`
      : appliedQuote
        ? compactCents(appliedQuote.rewardAmountCents)
        : null

  return (
    <section
      className="mx-auto mb-16 flex w-full max-w-3xl flex-col gap-5 font-sans text-[#0B0B0F] sm:gap-6"
      data-testid="weight-loss-pricing"
      aria-labelledby="weight-loss-pricing-title"
    >
      <h1
        id="weight-loss-pricing-title"
        className="text-xl font-medium leading-7 tracking-[-0.025em] text-gray-900 sm:text-2xl sm:leading-8"
      >
        Your weight loss plan
      </h1>

      <div className="-mx-6 overflow-hidden border-y border-gray-200 sm:mx-0 sm:rounded-xl sm:border">
        <div className="flex flex-col px-6 pt-6">
          <div className="flex flex-col gap-1 pb-[18px]">
            <p className="text-xs font-bold uppercase leading-4 tracking-wide">
              Weight loss plan
            </p>
            <h2 className="text-lg font-semibold leading-6">
              Initial assessment
            </h2>
          </div>

          {appliedQuote && (
            <div
              className="flex items-center justify-between gap-4 border-t border-gray-200 py-3.5"
              data-testid="weight-loss-offer-applied"
            >
              <div className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faTag} className="size-4 shrink-0" />
                <div className="space-y-0.5">
                  <p className="text-base font-medium leading-6 text-gray-900">
                    {reward?.title ?? 'Your offer'}
                  </p>
                  <p className="text-sm leading-5 text-gray-600">
                    {offerValue} off your assessment
                  </p>
                </div>
              </div>
              <span className="shrink-0 text-base font-semibold leading-6">
                −{compactCents(appliedQuote.rewardAmountCents)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 border-t border-gray-200 pb-[22px] pt-[18px]">
            <div className="space-y-0.5">
              <h2 className="text-lg font-semibold leading-6">Due today</h2>
              <p className="text-sm leading-5 text-gray-600">
                Refunded if you’re not eligible.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2.5">
              {appliedQuote && (
                <span
                  className="text-base font-medium leading-6 text-gray-400 line-through"
                  data-testid="weight-loss-original-price"
                >
                  {compactCents(assessmentCents)}
                </span>
              )}
              <span className="rounded-md bg-[#F2FF5E] px-2.5 py-0.5">
                <span
                  className="font-display text-4xl font-extrabold leading-10 tracking-[-0.03em]"
                  data-testid="weight-loss-due-today"
                >
                  {compactCents(dueTodayCents)}
                </span>
              </span>
            </div>
          </div>
          <div className="h-px w-full shrink-0 bg-gray-200" />
        </div>

        <div className="flex flex-col gap-5 px-6 pb-6 pt-5">
          <ul className="flex flex-col gap-3 text-[15px] leading-[22px] text-gray-900">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-3 sm:items-center">
                <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F6]">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" className="shrink-0">
                    <path
                      d="M4 12.5l5 5L20 6.5"
                      fill="none"
                      stroke="#0B0B0F"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-start gap-2.5 rounded-lg bg-[#F4F4F6] px-4 py-3 text-sm leading-5 text-gray-600">
            <FontAwesomeIcon
              icon={faCamera}
              className="mt-0.5 size-4 shrink-0"
            />
            <p>Your doctor may ask for a full body photo to assess you properly.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 text-sm leading-5 text-gray-900 sm:px-1">
        <p className="flex items-start gap-2.5">
          <FontAwesomeIcon
            icon={faArrowTurnDownLeft}
            className="mt-0.5 size-4 shrink-0 text-gray-500"
          />
          <span>
            Not eligible for the plan? We refund your{' '}
            {compactCents(assessmentCents)} assessment.
          </span>
        </p>
        <p className="flex items-center gap-2.5">
          <FontAwesomeIcon
            icon={faCircleXmark}
            className="size-4 shrink-0 text-gray-500"
          />
          <span>Cancel your plan anytime.</span>
        </p>
      </div>

      <div className="-mx-6 flex flex-col gap-3 border-t border-gray-200 px-6 pt-5 sm:mx-0 sm:flex-row sm:gap-0 sm:divide-x sm:divide-gray-200 sm:px-0">
        <div className="flex items-center gap-2.5 sm:flex-1 sm:flex-col sm:items-start sm:gap-1 sm:pl-1 sm:pr-5">
          <span className="flex items-center gap-2.5 font-display text-lg font-bold leading-6 tracking-tight sm:gap-1.5 sm:text-xl sm:leading-[26px]">
            {RATINGS.stars}
            <svg aria-hidden="true" viewBox="0 0 24 24" className="size-3.5 shrink-0 sm:size-4">
              <path
                d="M12 2.5l2.9 6.1 6.7.8-4.9 4.6 1.3 6.6L12 17.3l-6 3.3 1.3-6.6L2.4 9.4l6.7-.8z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className="text-[13px] leading-[18px] text-gray-500">
            Average patient rating
          </span>
        </div>
        <div className="flex items-center gap-2.5 sm:flex-1 sm:flex-col sm:items-start sm:gap-1 sm:px-5">
          <span className="font-display text-lg font-bold leading-6 tracking-tight sm:text-xl sm:leading-[26px]">
            {RATINGS.patients.toLocaleString('en-AU')}+
          </span>
          <span className="text-[13px] leading-[18px] text-gray-500">
            Patients across Australia
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 sm:flex-1 sm:flex-col sm:items-start sm:gap-1 sm:pl-5 sm:pr-1">
          <span className="whitespace-nowrap font-display text-lg font-bold leading-6 tracking-tight sm:text-xl sm:leading-[26px]">
            AHPRA-registered
          </span>
          <span className="text-[13px] leading-[18px] text-gray-500">
            Australian doctors
          </span>
        </div>
      </div>
    </section>
  )
}
